// Per-person preferences, stored server-side so they follow the person to every device (shell-ia §9.5; SPEC C29: `pref:<key>` rows in
// ui_layouts, served by `GET/PUT /api/econic/me/prefs[/{key}]` and bundled in `GET /api/econic/shell`). WP-SHELL.
//
// First paint reads a localStorage mirror (`econic.prefs:<userId>`, per person, so the next person on a shared machine never sees the
// last one's choices); the server answer replaces it. A write is applied at once, mirrored, then sent; if the stack does not serve the
// endpoint yet, the choice stays on this device and the next server answer that carries the key wins.
// Writes of one key are serialised: while a PUT is in flight, later values wait and only the LATEST is sent when it settles (two quick
// writes used to race on the server's layout version and the second answered 409, leaving the screen and the server disagreeing). A 409
// that still happens (another tab or device wrote the key) is retried once with the latest value.
//
// Keys (no `pref:` prefix here): ui.display · ui.theme · ui.look · ui.agentFace · ui.backdrop · ui.locale · ui.landing · ui.department · ui.density · econic.chat ·
// search.recent · notify · persona · today.view (Simple | Full) · today.desks (legacy) · today.board (My workspace's widget board, in order).
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ApiError, useApi, useAuth, useRealtimeEvent } from "@ncp/api-client";
import { useShellSummary } from "../shell/useShellSummary";
import { createPrefWriter } from "./writer";

const MIRROR_PREFIX = "econic.prefs:";
const MAX_BYTES = 8 * 1024;

export type PrefValues = Record<string, unknown>;

interface PrefsContextValue {
  values: PrefValues;
  /** The server answered at least once (or the bundle carried the prefs). */
  synced: boolean;
  /** The stack has no preferences endpoint: choices stay on this device. */
  deviceOnly: boolean;
  set: (key: string, value: unknown) => void;
}

const PrefsContext = createContext<PrefsContextValue | null>(null);

/** `pref:ui.theme` and `ui.theme` are the same key. */
export const prefKey = (key: string) => (key.startsWith("pref:") ? key.slice(5) : key);

function normalise(raw: unknown): PrefValues {
  if (!raw || typeof raw !== "object") return {};
  const out: PrefValues = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) out[prefKey(k)] = v;
  return out;
}

function readMirror(userId: string | null): PrefValues {
  if (!userId) return {};
  try {
    return normalise(JSON.parse(localStorage.getItem(MIRROR_PREFIX + userId) ?? "{}"));
  } catch {
    return {};
  }
}

function writeMirror(userId: string | null, values: PrefValues): void {
  if (!userId) return;
  try {
    localStorage.setItem(MIRROR_PREFIX + userId, JSON.stringify(values));
  } catch {
    // Private windows and blocked storage: the server copy still applies.
  }
}

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const api = useApi();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const shell = useShellSummary();
  const [values, setValues] = useState<PrefValues>(() => readMirror(userId));
  const [owner, setOwner] = useState(userId);
  const [synced, setSynced] = useState(false);
  const [deviceOnly, setDeviceOnly] = useState(false);
  // Keys written on this device since the last server answer: a stale answer must not undo them.
  const pending = useRef(new Map<string, unknown>());
  // One PUT per key at a time; the newest value is sent when the running one settles (writer.ts).
  const writer = useMemo(() => createPrefWriter({
    put: (k, v) => api.put(`/api/econic/me/prefs/${encodeURIComponent(k)}`, { value: v }),
    wanted: (k) => (pending.current.has(k) ? { has: true, value: pending.current.get(k) } : { has: false }),
    done: (k) => { pending.current.delete(k); },
    failed: (_k, e) => { if (e instanceof ApiError && (e.status === 404 || e.status === 405)) setDeviceOnly(true); },
    isConflict: (e) => e instanceof ApiError && e.status === 409,
  }), [api]);

  if (owner !== userId) {
    setOwner(userId);
    setValues(readMirror(userId));
    setSynced(false);
    pending.current.clear();
    writer.reset();
  }

  const adopt = useCallback((server: PrefValues) => {
    setValues((current) => {
      const next: PrefValues = { ...current, ...server };
      for (const [k, v] of pending.current) next[k] = v;
      writeMirror(userId, next);
      return next;
    });
    setSynced(true);
  }, [userId]);

  // The bundle carries the prefs; a stack without the bundle is asked for the prefs alone.
  const bundled = shell.data?.prefs;
  useEffect(() => {
    if (bundled) adopt(normalise(bundled));
  }, [bundled, adopt]);

  useEffect(() => {
    if (!userId || !shell.unavailable || synced) return;
    let cancelled = false;
    api.get<PrefValues>("/api/econic/me/prefs")
      .then((r) => { if (!cancelled) adopt(normalise(r)); })
      .catch((e: unknown) => { if (!cancelled && e instanceof ApiError && (e.status === 404 || e.status === 405)) setDeviceOnly(true); });
    return () => { cancelled = true; };
  }, [api, userId, shell.unavailable, synced, adopt]);

  // Another tab or device changed a preference.
  useRealtimeEvent<{ key?: string; value?: unknown }>("ui.pref.changed", (p) => {
    if (!p?.key) return;
    const k = prefKey(p.key);
    if (pending.current.has(k)) return;
    setValues((current) => {
      const next = { ...current, [k]: p.value };
      writeMirror(userId, next);
      return next;
    });
  }, !!userId);

  const set = useCallback((key: string, value: unknown) => {
    const k = prefKey(key);
    let body: string;
    try {
      body = JSON.stringify({ value });
    } catch {
      return;
    }
    if (body.length > MAX_BYTES) return;
    pending.current.set(k, value);
    setValues((current) => {
      const next = { ...current, [k]: value };
      writeMirror(userId, next);
      return next;
    });
    if (deviceOnly || !userId) return;
    writer.flush(k);
  }, [deviceOnly, userId, writer]);

  const value = useMemo<PrefsContextValue>(() => ({ values, synced, deviceOnly, set }), [values, synced, deviceOnly, set]);
  return <PrefsContext.Provider value={value}>{children}</PrefsContext.Provider>;
}

function usePrefsContext(): PrefsContextValue {
  const ctx = useContext(PrefsContext);
  if (!ctx) throw new Error("usePref must be used inside <PreferencesProvider> (the ECONIC shell).");
  return ctx;
}

/** All preferences and whether the server copy has arrived. */
export function usePrefs(): Pick<PrefsContextValue, "values" | "synced" | "deviceOnly"> {
  const { values, synced, deviceOnly } = usePrefsContext();
  return { values, synced, deviceOnly };
}

/**
 * One preference: its value (or `fallback` when unset or when `accept` refuses the stored value) and a setter that stores it for the
 * person on every device.
 */
export function usePref<T>(key: string, fallback: T, accept?: (v: unknown) => v is T): [T, (v: T) => void] {
  const { values, set } = usePrefsContext();
  const k = prefKey(key);
  const raw = values[k];
  const current = raw === undefined || raw === null || (accept && !accept(raw)) ? fallback : (raw as T);
  const setter = useCallback((v: T) => set(k, v), [set, k]);
  return [current, setter];
}

/** The setter alone, for code that must not re-render on the value. */
export function useSetPref(): (key: string, value: unknown) => void {
  return usePrefsContext().set;
}

/** Outside the shell (the landing, the os and legacy flags) a preference reads as its fallback and writes nowhere. */
export function usePrefOptional<T>(key: string, fallback: T): T {
  const ctx = useContext(PrefsContext);
  const raw = ctx?.values[prefKey(key)];
  return raw === undefined || raw === null ? fallback : (raw as T);
}
